import React, { useState } from "react";
import { observer } from "mobx-react";
import { FieldState } from "formstate";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import {
  AutoCompleteProps,
  AutoComplete
} from "@fidget/common/components/form/autoComplete/autoComplete";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { useStore } from "../../../models";
import { IMessageWidgetConfig } from "../../../models/messageWidgets/MessageWidgetConfig";
import { LanguageOption } from "@fidget/common/components/form/languagePicker/languagePicker";

interface Props extends Omit<Partial<AutoCompleteProps<IMessageWidgetConfig>>, "onChange"> {
  messageWidgetId?: string;
  locale?: LanguageOption;
  onChange(widget: IMessageWidgetConfig): void;
}

export const MessageWidgetsPicker = observer(
  ({ messageWidgetId, onChange, locale, className, ...autoCompleteProps }: Props) => {
    const [inputField] = useState(new FieldState(""));

    const { messageWidgetsStore } = useStore();
    const messageWidgets = locale
      ? messageWidgetsStore.getLocaleMessageWidgets(locale)
      : messageWidgetsStore.getAllWidgets();

    const handleChange = (val: IMessageWidgetConfig | "") => {
      if (val) {
        onChange(val);
      }
    };

    const value = messageWidgetId && messageWidgets.find(({ id }) => id === messageWidgetId)?.name;

    return (
      <AutoComplete<IMessageWidgetConfig>
        onChange={handleChange}
        itemsProvider={
          messageWidgets
            ? () =>
                messageWidgets.filter((widget) =>
                  caseInsensitiveIncludes(widget.name, inputField.$)
                )
            : () => messageWidgets
        }
        itemToString={(val) => val?.name ?? ""}
        inputValue={value ?? ""}
        readOnly
        placeholder={intl.formatMessage("messageWidgetPicker.placeholder")}
        emptyPlaceholder={intl.formatMessage("messageWidgetPicker.empty")}
        className={className}
        {...autoCompleteProps}
      >
        <AgentInput
          field={inputField}
          placeholder={intl.formatMessage("messageWidgetPicker.input.placeholder")}
        />
      </AutoComplete>
    );
  }
);
